import { ChartOptions } from 'chart.js'
import { PartialEventContext } from 'chartjs-plugin-annotation'
import { getFontSize } from '../../../../../utils/getFontSize'
import { roundTicksTo1Decimal } from '../../../utils/roundTicksTo1Decimal'
import { ChartModes } from '../types/types'

interface ConsumptionChartOptionsProps {
	activeElement: number
	chartModes: ChartModes
	xLabels: string[]
	isDarkMode: boolean
	onHover?: (index: number) => void
}

export const getConsumptionChartOptions = ({
	activeElement,
	chartModes,
	xLabels,
	isDarkMode,
	onHover,
}: ConsumptionChartOptionsProps): ChartOptions => {
	const leftTickColor = isDarkMode ? '#3cb49f' : '#127d27'
	const rightTickColor = isDarkMode ? '#127d27' : '#40bfa9'
	const gridColor = isDarkMode ? '#144448' : '#f5f5f5'
	const xTickColor = isDarkMode ? '#e3e3e3' : '#858585'
	const tooltipColor = isDarkMode ? '#3cb49f' : '#127d27'
	const tooltipBackgroundColor = isDarkMode ? '#0d2d30' : '#ffffff'
	const tooltipTextColor = isDarkMode ? '#e3e3e3' : '#858585'
	const fontSize = getFontSize()

	return {
		elements: {
			point: {
				hitRadius: 5,
			},
			line: {
				tension: 0.5,
			},
		},
		devicePixelRatio: 2,
		maintainAspectRatio: false,
		layout: {
			autoPadding: true,
			padding: {
				left: 14,
				right: !chartModes.exchangePrices && !chartModes.transferPrices ? 45 : 14,
			},
		},
		scales: {
			x: {
				stacked: true,
				ticks: {
					maxTicksLimit: 12,
					minRotation: 45,
					maxRotation: 45,
					color: xTickColor,
					font: {
						size: fontSize,
					},
				},
				grid: {
					display: false,
				},
				border: {
					display: false,
				},
				grace: '100%',
			},
			y: {
				stacked: true,
				min: 0,
				ticks: {
					color: leftTickColor,
					callback: roundTicksTo1Decimal,
					display: true,
					font: {
						size: fontSize,
					},
				},
				grace: '100%',
				grid: {
					color: gridColor,
					drawTicks: false,
				},
				border: {
					display: false,
				},
			},
			prices: {
				position: 'right',
				display: chartModes.exchangePrices || chartModes.transferPrices,
				ticks: {
					color: rightTickColor,
					callback: roundTicksTo1Decimal,
					display: chartModes.exchangePrices || chartModes.transferPrices,
					font: {
						size: fontSize,
					},
				},
				grid: {
					display: false,
				},
				suggestedMin: (context: any) => {
					const datasets = context.chart.data.datasets.filter((ds: any) => ds.yAxisID === 'prices')
					if (datasets.length > 0) {
						const allValues = datasets.flatMap((ds: any) =>
							(ds.data as (number | null | undefined)[]).filter(
								(val: any): val is number => val !== null && val !== undefined
							)
						)
						if (allValues.length > 0) {
							return Math.min(0, ...allValues)
						}
					}
					return 0
				},
				grace: '100%',
				stacked: false,
				border: {
					display: false,
				},
			},
			production: {
				position: 'right',
				display: false,
				ticks: {
					color: rightTickColor,
					callback: roundTicksTo1Decimal,
					display: chartModes.production,
					font: {
						size: fontSize,
					},
				},
				grid: {
					drawOnChartArea: false,
				},
				min: 0,
				grace: '100%',
				stacked: true,
				border: {
					display: false,
				},
			},
		},
		plugins: {
			tooltip: {
				titleFont: {
					size: fontSize + 2,
					weight: 'bold',
				},
				bodyFont: {
					size: fontSize,
				},
				enabled: true,
				callbacks: {
					afterTitle(tooltipItems) {
						if (tooltipItems && tooltipItems.length > 0 && onHover !== undefined) {
							if (tooltipItems[0].dataIndex !== activeElement) {
								onHover(tooltipItems[0].dataIndex)
							}
						}
					},
				},
			},
			legend: { display: false },
			annotation: {
				clip: false,
				annotations: [
					{
						type: 'line',
						xMin: activeElement,
						xMax: activeElement,
						borderColor: tooltipColor,
						borderWidth: 2,
						drawTime: 'beforeDatasetsDraw',
						display: true,
					},
					{
						type: 'label',
						xValue: activeElement,
						yValue: (context: PartialEventContext) => context.chart.scales.y.max * 0.9,
						borderRadius: 6,
						borderWidth: 2,
						content: xLabels[activeElement],
						borderColor: tooltipColor,
						color: tooltipTextColor,
						backgroundColor: tooltipBackgroundColor,
						font: {
							size: fontSize,
						},
						drawTime: 'beforeDatasetsDraw',
						display: true,
					},
				],
			},
		},
	}
}
